/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {PageState} from '@ion/applications/src/types/pages';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {
  DeviceFileFilters,
  DeviceFiles
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {
  AsyncState
} from '@/types/redux';
import {
  ADD_DEVICE_FILE,
  ADD_DEVICE_FILE_ERROR,
  ADD_DEVICE_FILE_REQUEST,
  ADD_DEVICE_FILE_RESPONSE,
  ADD_MODBUS_CONFIG,
  ADD_MODBUS_CONFIG_ERROR,
  ADD_MODBUS_CONFIG_REQUEST,
  ADD_MODBUS_CONFIG_RESPONSE,
  AUDIT_DEVICE_FILE,
  AUDIT_DEVICE_FILE_SHOW_CHANGES_DIALOG,
  DELETE_DEVICE_FILE,
  DELETE_DEVICE_FILE_ERROR,
  DELETE_DEVICE_FILE_REQUEST,
  DELETE_DEVICE_FILE_RESPONSE,
  DEVICE_FILES_GRID_REFRESHED,
  DOWNLOAD_FILE,
  DOWNLOAD_RELEASE_NOTE,
  EDIT_DEVICE_FILE,
  EDIT_DEVICE_FILE_ERROR,
  EDIT_DEVICE_FILE_REQUEST,
  EDIT_DEVICE_FILE_RESPONSE,
  EDIT_MODBUS_CONFIG,
  EDIT_MODBUS_CONFIG_ERROR,
  EDIT_MODBUS_CONFIG_REQUEST,
  EDIT_MODBUS_CONFIG_RESPONSE,
  EDIT_PEG_WIZARD_FILE,
  GET_DEVICE_FILES_ERROR,
  GET_DEVICE_FILES_REQUEST,
  GET_DEVICE_FILES_RESPONSE,
  INITIALIZE_DEVICE_FILES,
  RESET_DEVICE_FILE_SEARCH_QUERIES,
  RESET_DEVICE_FILE_STATE,
  SET_GRID_COLUMNS,
  SET_GRID_PAGE_SIZE,
  SET_VIEW_ONLY_THIS_ACCOUNT,
  SET_VIEW_SUB_GROUPS,
  UPDATE_DEVICE_FILE_SEARCH_QUERIES,
  UPDATE_GRID_COLUMNS,
  VIEW_DEVICE_FILE,
  VIEW_DEVICE_FILE_ERROR,
  VIEW_DEVICE_FILE_REQUEST,
  VIEW_DEVICE_FILE_RESPONSE
} from '../actions/devicefiles';

export interface DeviceFilesAction {
  type: string;
  result?: any;
  error?: ServiceError;
  show?: boolean;
  isView?: boolean;
};

export interface DeviceFileAction extends DeviceFilesAction {
  deviceFile?: DeviceFiles;
  deviceFiles?: DeviceFiles[];
  searchParams?: DeviceFileFilters;
};

interface DeviceFilesActionState extends AsyncState {
  selected: DeviceFiles | DeviceFiles[] | null;
  result: DeviceFiles | DeviceFiles[] | null;
  isEditing: boolean;
  record?: DeviceFiles | DeviceFiles[] | null;
  isView?: boolean;
};

export interface DeviceFilesAuditState extends DeviceFilesActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

export interface DeviceFilesDestroyState extends AsyncState {
  selected: DeviceFiles | null;
  result: DeviceFiles | null;
  showDialog: boolean;
};

export interface DeviceFilesState extends PageState {
  devicefiles: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number,
    includeOnlyThisAccount: boolean
  },
  add: DeviceFilesActionState,
  view: DeviceFilesActionState,
  edit: DeviceFilesActionState,
  audit: DeviceFilesAuditState,
  search: DeviceFileFilters,
  showExportFile: boolean,
  downloadFileType: string,
  showExportReleaseNote: boolean,
  downloadFiles: DeviceFiles,
  downloadReleaseNotes: DeviceFiles,
  destroy: DeviceFilesDestroyState,
  editModbusConfig: DeviceFilesActionState,
  addModbusConfig: DeviceFilesActionState,
  editPegWizard: DeviceFilesActionState
};

const clearSearch = (): DeviceFileFilters => ({
  name       : null,
  platformId : null,
  version    : null,
  status     : null,
  appId      : null
});

const clearDestroyState = (): DeviceFilesDestroyState => ({
  showDialog : false,
  selected   : null,
  loading    : false,
  error      : null,
  result     : null
});

// defines the initial state for the reducer ...
export const initialState: DeviceFilesState = {
  isInitialized : false,
  devicefiles   : {
    error   : null,
    loading : false
  },
  grid : {
    columnState            : null,
    preferedColumnState    : null,
    hasUpdate              : false,
    includeSubGroups       : false,
    pageSize               : gridDefaultPageSize,
    includeOnlyThisAccount : false
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  view : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  addModbusConfig : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  editModbusConfig : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  editPegWizard : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  },
  search                : clearSearch(),
  showExportFile        : false,
  downloadFileType      : '',
  showExportReleaseNote : false,
  downloadFiles         : null,
  downloadReleaseNotes  : null,
  destroy               : clearDestroyState()
};

// defines the preference key for the provisioning device files page ...
export const PREFERENCE = 'schedule-devicefiles';

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_DEVICE_FILES] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.search                   = clearSearch();
  },
  [RESET_DEVICE_FILE_STATE] : draft => {
    draft.destroy = clearDestroyState();
  },
  [SET_GRID_COLUMNS] : ({grid, devicefiles}: DeviceFilesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : devicefiles.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [DEVICE_FILES_GRID_REFRESHED] : (
    {add, edit, destroy, editModbusConfig, addModbusConfig}: DeviceFilesState
  ) => {
    add.result = null;
    edit.result = null;
    destroy.result = null;
    addModbusConfig.result = null;
    editModbusConfig.result = null;
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: DeviceFilesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_DEVICE_FILES_REQUEST] : ({devicefiles}: DeviceFilesState) => {
    devicefiles.loading = true;
  },
  [GET_DEVICE_FILES_RESPONSE] : ({devicefiles}: DeviceFilesState) => {
    devicefiles.loading = false;
  },
  [GET_DEVICE_FILES_ERROR] : ({devicefiles}: DeviceFilesState, {error}: DeviceFileAction) => {
    devicefiles.error = error;
    devicefiles.loading = false;
  },
  [ADD_DEVICE_FILE] : ({add}: DeviceFilesState, {show}: DeviceFileAction) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_DEVICE_FILE_REQUEST] : ({add}: DeviceFilesState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_DEVICE_FILE_RESPONSE] : ({add}: DeviceFilesState, {file}: DeviceFilesAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = file;
  },
  [ADD_DEVICE_FILE_ERROR] : ({add}: DeviceFilesState, {error}: DeviceFileAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [VIEW_DEVICE_FILE] : ({view}: DeviceFilesState, {deviceFile, show}: DeviceFileAction) => {
    view.selected  = show ? deviceFile : null;
    view.isEditing = show;
    view.result    = null;
  },
  [VIEW_DEVICE_FILE_REQUEST] : ({view}: DeviceFilesState) => {
    view.loading = true;
    view.error   = view.result = null;
  },
  [VIEW_DEVICE_FILE_RESPONSE] : ({view}: DeviceFilesState, {result}: DeviceFileAction) => {
    view.loading   = false;
    view.result    = result;
  },
  [VIEW_DEVICE_FILE_ERROR] : ({view}: DeviceFilesState, {error}: DeviceFileAction) => {
    view.loading   = false;
    view.error     = error;
  },
  [EDIT_DEVICE_FILE] : ({edit}: DeviceFilesState, {deviceFile, show, isView}: DeviceFileAction) => {
    edit.selected  = (show || isView) ? deviceFile : null;
    edit.isEditing = show;
    edit.isView    = isView;
    edit.result    = null;
  },
  [EDIT_DEVICE_FILE_REQUEST] : ({edit}: DeviceFilesState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_DEVICE_FILE_RESPONSE] : ({edit}: DeviceFilesState, {deviceFile}: DeviceFileAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = deviceFile;
  },
  [EDIT_DEVICE_FILE_ERROR] : ({edit}: DeviceFilesState, {error}: DeviceFileAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [ADD_MODBUS_CONFIG] : ({addModbusConfig}: DeviceFilesState, {show}: DeviceFileAction) => {
    addModbusConfig.isEditing = show;
    addModbusConfig.result = null;
  },
  [ADD_MODBUS_CONFIG_REQUEST] : ({addModbusConfig}: DeviceFilesState) => {
    addModbusConfig.loading = true;
    addModbusConfig.error = addModbusConfig.result = null;
  },
  [ADD_MODBUS_CONFIG_RESPONSE] : ({addModbusConfig}: DeviceFilesState, {file}: DeviceFilesAction) => {
    addModbusConfig.loading = false;
    addModbusConfig.isEditing = false;
    addModbusConfig.result = file;
  },
  [ADD_MODBUS_CONFIG_ERROR] : ({addModbusConfig}: DeviceFilesState, {error}: DeviceFileAction) => {
    addModbusConfig.loading = false;
    addModbusConfig.error = error;
  },
  [EDIT_MODBUS_CONFIG] : ({editModbusConfig}: DeviceFilesState, {deviceFile, show}: DeviceFileAction) => {
    editModbusConfig.selected  = show ? deviceFile : null;
    editModbusConfig.isEditing = show;
    editModbusConfig.result    = null;
  },
  [EDIT_MODBUS_CONFIG_REQUEST] : ({editModbusConfig}: DeviceFilesState) => {
    editModbusConfig.loading = true;
    editModbusConfig.error   = editModbusConfig.result = null;
  },
  [EDIT_MODBUS_CONFIG_RESPONSE] : ({editModbusConfig}: DeviceFilesState, {deviceFile}: DeviceFileAction) => {
    editModbusConfig.loading   = false;
    editModbusConfig.isEditing = false;
    editModbusConfig.result    = deviceFile;
  },
  [EDIT_MODBUS_CONFIG_ERROR] : ({editModbusConfig}: DeviceFilesState, {error}: DeviceFileAction) => {
    editModbusConfig.loading = false;
    editModbusConfig.error   = error;
  },
  [EDIT_PEG_WIZARD_FILE] : ({editPegWizard}: DeviceFilesState, {deviceFile, show}: DeviceFileAction) => {
    editPegWizard.selected  = show ? deviceFile : null;
    editPegWizard.isEditing = show;
  },
  [AUDIT_DEVICE_FILE] : ({audit}: DeviceFilesState,
    {deviceFile, show}: DeviceFileAction) => {
    audit.selected  = show ? deviceFile : null;
    audit.isEditing = show;
  },
  [AUDIT_DEVICE_FILE_SHOW_CHANGES_DIALOG] : ({audit}: DeviceFilesState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord       = auditRecord;
  },
  [UPDATE_DEVICE_FILE_SEARCH_QUERIES] : ({search}: DeviceFilesState, {searchParams}) => {
    search.name       = searchParams?.name || null;
    search.platformId = searchParams?.platformId || null;
    search.version    = searchParams?.version || null;
    search.status     = searchParams?.status || null;
    search.appId      = searchParams?.appId || null;
  },
  [RESET_DEVICE_FILE_SEARCH_QUERIES] : draft => {
    draft.search = clearSearch();
  },
  [DOWNLOAD_FILE] : (draft: DeviceFilesState, {deviceFile, fileType, show}) => {
    draft.showExportFile   = show;
    draft.downloadFileType = fileType;
    draft.downloadFiles    = deviceFile;
  },
  [DOWNLOAD_RELEASE_NOTE] : (draft: DeviceFilesState, {deviceFile, show}) => {
    draft.showExportReleaseNote = show;
    draft.downloadReleaseNotes  = deviceFile;
  },
  [DELETE_DEVICE_FILE] : ({destroy}: DeviceFilesState, {deviceFile, show}: DeviceFileAction) => {
    destroy.selected = show ? deviceFile : null;
    destroy.showDialog = show;
    destroy.result = null;
  },
  [DELETE_DEVICE_FILE_REQUEST] : ({destroy}: DeviceFilesState) => {
    destroy.loading = true;
    destroy.error = null;
    destroy.result = null;
  },
  [DELETE_DEVICE_FILE_RESPONSE] : ({destroy}: DeviceFilesState, {deviceFile}: DeviceFileAction) => {
    destroy.result = deviceFile;
    destroy.selected = null;
    destroy.loading = destroy.showDialog = false;
  },
  [DELETE_DEVICE_FILE_ERROR] : ({destroy}: DeviceFilesState, {error}: DeviceFileAction) => {
    destroy.error = error;
    destroy.result = null;
    destroy.loading = destroy.showDialog = false;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: DeviceFilesState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_VIEW_ONLY_THIS_ACCOUNT] : ({grid}: DeviceFilesState, {viewOnlyThisAccount}) => {
    grid.includeOnlyThisAccount = viewOnlyThisAccount;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: DeviceFilesState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: DeviceFilesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
